import { Link, useMatchRoute, useRouteContext } from "@tanstack/react-router";
import {
	graphql,
	useFragment,
	useMutation,
	useRefetchableFragment,
} from "react-relay";
import type { sidebarPreviewEvents$key } from "./__generated__/sidebarPreviewEvents.graphql";
import { Card } from "@/components/ui/card";
import type { sidebarPreview$key } from "./__generated__/sidebarPreview.graphql";
import type { sidebarPreviewLeaderboard$key } from "./__generated__/sidebarPreviewLeaderboard.graphql";
import {
	Select,
	SelectTrigger,
	SelectContent,
	SelectItem,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/utils/cn";
import type { sidebarPreviewLeaderboardQuery } from "./__generated__/sidebarPreviewLeaderboardQuery.graphql";
import { useEffect, useState, useTransition } from "react";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/icon";
import { PlusIcon } from "@heroicons/react/16/solid";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

export function SidebarPreviewEvents({
	club: clubFragment,
}: {
	club: sidebarPreviewEvents$key;
}) {
	const { club } = useRouteContext({ from: "__root__" });
	const [deleteEvent] = useMutation(graphql`
     mutation sidebarPreview_DeleteEventMutation($id: ID!) {
       deleteEvent(id: $id) {
     		id @deleteRecord
     	}
     }
	`);
	const { events } = useFragment<sidebarPreviewEvents$key>(
		graphql`
    fragment sidebarPreviewEvents on Club {
      events(orderBy: CHRONOLOGICAL, filterBy: UPCOMING, first: 3) {
        edges {
          node {
            id
            slug
            title
            startDate
            endDate
          }
          cursor
        }
      }
    }
  `,
		clubFragment,
	);
	const isAdmin =
		club?.permissions?.some((p) => p.slug === "CLUB_ADMIN") || false;

	if (!events.edges.filter((e) => Boolean(e.node)).length && isAdmin) {
		return (
			<Card className="p-6">
				<div className="flex flex-col items-center justify-between gap-3">
					<div className="flex flex-row items-center justify-between w-full">
						<span className="font-semibold text-base/6 text-balance text-card-foreground">
							Eventos
						</span>

						{isAdmin && (
							<Link to="/eventos/novo">
								<Button>
									<PlusIcon />
									Novo
								</Button>
							</Link>
						)}
					</div>

					<div className="my-6 w-full flex flex-col gap-3 items-center justify-center text-sm text-muted-foreground">
						<Icon name="CircleOff" className="size-5" />
						Nenhum evento futuro encontrado
					</div>
				</div>
			</Card>
		);
	}

	if (!events.edges.filter((e) => Boolean(e.node)).length) {
		return null;
	}

	return (
		<Card className="p-6">
			<div className="flex flex-col items-center justify-between gap-3">
				<div className="flex flex-row items-center justify-between w-full">
					<span className="font-semibold text-base/6 text-balance text-card-foreground">
						Eventos
					</span>

					{isAdmin && (
						<Link to="/eventos/novo">
							<Button>
								<PlusIcon />
								Novo
							</Button>
						</Link>
					)}
				</div>

				<div className="mt-3 w-full flex flex-col gap-3">
					{events.edges
						.filter((e) => Boolean(e.node))
						.map(({ node, cursor }) => (
							<div
								key={cursor}
								className="flex flex-row items-center justify-between"
							>
								<div className="w-full flex flex-row items-center justify-start gap-3 flex-1">
									<div className="flex flex-col items-center justify-center bg-accent aspect-square size-14 rounded">
										<span className="text-base/6 font-semibold text-accent-foreground">
											{format(new Date(node.startDate), "dd")}
										</span>
										<span className="text-xs uppercase font-medium text-accent-foreground/70">
											{format(new Date(node.startDate), "MMM", {
												locale: ptBR,
											})}
										</span>
									</div>

									<div className="flex flex-col">
										<span className="font-medium text-base/6 text-foreground">
											{node.title}
										</span>

										<div className="flex items-center justify-start gap-1 text-muted-foreground">
											<Icon name="Clock9" className="size-3" />
											<span className="text-sm/5">
												{format(new Date(node.startDate), "HH:mm")}
												{" - "}
												{format(new Date(node.endDate), "HH:mm")}
											</span>
										</div>
									</div>
								</div>

								{isAdmin && (
									<DropdownMenu>
										<DropdownMenuTrigger asChild>
											<Button variant="ghost" size="icon">
												<Icon name="EllipsisVertical" />
											</Button>
										</DropdownMenuTrigger>

										<DropdownMenuContent>
											<Link
												to="/eventos/$slug/editar"
												params={{ slug: node.slug }}
											>
												<DropdownMenuItem>
													<Icon name="Pencil" />
													Editar Evento
												</DropdownMenuItem>
											</Link>
											<DropdownMenuItem
												onClick={() => {
													if (
														confirm(
															"Tem certeza que desja excluir este event? Essa operação não poderá ser desfeita.",
														)
													) {
														deleteEvent({
															variables: { id: node.id },
															onCompleted: () => {
																toast.success("Evento deletado com sucesso!");
															},
														});
													}
												}}
											>
												<Icon name="Trash2" />
												Deletar Evento
											</DropdownMenuItem>
										</DropdownMenuContent>
									</DropdownMenu>
								)}
							</div>
						))}
				</div>
			</div>
		</Card>
	);
}

export function SidebarPreviewLeaderboard({
	club,
}: {
	club: sidebarPreviewLeaderboard$key;
}) {
	const [orderBy, setOrderBy] = useState<
		"current_month" | "last_month" | "last_3_months" | "total"
	>("current_month");
	const [isPending, startTransition] = useTransition();
	const [leaderboard, refetch] = useRefetchableFragment<
		sidebarPreviewLeaderboardQuery,
		sidebarPreviewLeaderboard$key
	>(
		graphql`
     fragment sidebarPreviewLeaderboard on Club
     @refetchable(queryName: "sidebarPreviewLeaderboardQuery")
     @argumentDefinitions(orderBy: { type: RankingUserOrderBy })
     {
      ranking {
        users(first: 5, orderBy: $orderBy, order: DESC) {
          edges {
            node {
              user {
                displayName
                photo {
                  signedUrl
                }
                username
                avatarFallback
              }
              totalPoints
              totalPointsCurrentMonth
              totalPointsLast3Months
              totalPointsLastMonth
            }
            cursor
          }
        }
      }
     }
   `,
		club,
	);

	const updateOrderBy = (
		newOrderBy: "current_month" | "last_month" | "last_3_months" | "total",
	) => {
		setOrderBy(newOrderBy);
	};

	useEffect(() => {
		startTransition(() => {
			refetch(
				{
					orderBy:
						orderBy === "current_month"
							? "TOTAL_POINTS_CURRENT_MONTH"
							: orderBy === "last_3_months"
								? "TOTAL_POINTS_LAST_3_MONTHS"
								: orderBy === "last_month"
									? "TOTAL_POINTS_LAST_MONTH"
									: "TOTAL_POINTS",
				},
				{ fetchPolicy: "store-or-network" },
			);
		});
	}, [orderBy, refetch]);

	return (
		<Card className="p-6 pb-3">
			<div className="flex items-center justify-between gap-3">
				<span className="font-semibold text-base/6 text-balance foreground">
					Ranking
				</span>

				<Select
					value={orderBy}
					onValueChange={(value) => {
						updateOrderBy(
							value as
								| "current_month"
								| "last_month"
								| "last_3_months"
								| "total",
						);
					}}
				>
					<SelectTrigger>
						{orderBy === "current_month" && "Mês Atual"}
						{orderBy === "last_month" && "Mês Passado"}
						{orderBy === "last_3_months" && "Ult. 3 meses"}
						{orderBy === "total" && "Desde o início"}
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="current_month">Mês Atual</SelectItem>
						<SelectItem value="last_month">Mês Passado</SelectItem>
						<SelectItem value="last_3_months">Ult. 3 meses</SelectItem>
						<SelectItem value="total">Desde o início</SelectItem>
					</SelectContent>
				</Select>
			</div>

			{isPending ? (
				<div className="mt-3 py-8 flex items-center justify-center">
					<Spinner />
				</div>
			) : (
				<div className="flex flex-col divide-y divide-border">
					{leaderboard.ranking.users.edges.map(({ node, cursor }, index) => (
						<div
							key={cursor}
							className="py-3 flex flex-row items-center justify-between"
						>
							<div className="flex flex-row items-center justify-start gap-3">
								<span
									className={cn(
										"text-xl font-semibold text-muted-foreground",
										index === 0 && "text-yellow-500 dark:text-yellow-600",
										index === 1 && "text-[#9da3af] dark:text-[#9da3af]",
										index === 2 && "text-[#a16207] dark:text-[#a16207]",
									)}
								>
									{index + 1}
								</span>

								<div className="flex flex-row items-center justify-center gap-2">
									<Avatar>
										<AvatarImage src={node.user.photo?.signedUrl} />
										<AvatarFallback>{node.user.avatarFallback}</AvatarFallback>
									</Avatar>

									<Link
										className="text-sm foreground"
										to="/u/$username"
										params={{ username: node.user.username! }}
									>
										{node.user.displayName}
									</Link>
								</div>
							</div>

							<div className="font-medium text-green-700 dark:text-green-600">
								{`+ `}
								{orderBy === "total" ? node.totalPoints : null}
								{orderBy === "last_3_months"
									? node.totalPointsLast3Months
									: null}
								{orderBy === "current_month"
									? node.totalPointsCurrentMonth
									: null}
								{orderBy === "last_month" ? node.totalPointsLastMonth : null}
								<span className="text-xs font-normal ml-1 text-muted-foreground">
									pts
								</span>
							</div>
						</div>
					))}
				</div>
			)}
		</Card>
	);
}

export function SidebarPreview({ club }: { club: sidebarPreview$key }) {
	const match = useMatchRoute();
	const data = useFragment<sidebarPreview$key>(
		graphql`
      fragment sidebarPreview on Club
      @argumentDefinitions(orderBy: { type: RankingUserOrderBy })
      {
          ...sidebarPreviewEvents
          ...sidebarPreviewLeaderboard @arguments(orderBy: $orderBy)

          leaderboard: configuration(key: "LEADERBOARD")

          events(orderBy: CHRONOLOGICAL, filterBy: UPCOMING, first: 3) {
            edges {
              node {
                id
              }
            }
          }
          ranking {
            rankingUsers: users(first: 5) {
              edges {
               	node {
               	  totalPoints
               	}
              }
            }
          }
      }
  `,
		club,
	);

	const hasEvents = data.events.edges.filter((e) => Boolean(e.node)).length > 0;
	const hasLeaderboardUsers =
		data.ranking.rankingUsers.edges.filter((ed) => ed.node.totalPoints > 0)
			.length > 0;
	const isEventsRoute = match({ to: "/eventos" });
	const isLeaderboardEnabled =
		data.leaderboard === "ENABLED" || data.leaderboard === "1";

	if (isEventsRoute && (!isLeaderboardEnabled || !hasLeaderboardUsers)) {
		return null;
	} else if (!hasEvents && !hasLeaderboardUsers) {
		return null;
	}

	return (
		<div
			className={cn(
				"flex flex-col gap-6 w-[340px] min-w-[340px] max-w-[340px]",
				isEventsRoute ? "max-lg:hidden" : "max-xl:hidden",
			)}
		>
			{!isEventsRoute && <SidebarPreviewEvents club={data} />}

			{isLeaderboardEnabled && <SidebarPreviewLeaderboard club={data} />}
		</div>
	);
}
